/**
 * Spec 200 Fase 5 #5 — workers list hook.
 *
 * The relay broadcasts a `{ type: 'workers', list }` message after
 * `identify` and on every roster change. `RelayClient.onWorkers`
 * surfaces it; this hook caches the latest list in component state
 * so the technician multi-select can show real names instead of
 * raw ids.
 *
 * Web peers identify as `${technicianId}-web-${terminalId.slice(0, 8)}`
 * so the relay's worker-keyed socket map doesn't evict the mobile
 * peer. The roster the relay broadcasts therefore lists both — the
 * mobile slot and every paired web tab — as if they were independent
 * technicians. From a UX standpoint they are the SAME person, so we
 * dedup before exposing the list. See B10 in the post-explore backlog.
 */
import { useEffect, useState } from 'react';

import { relay, type WorkerListEntry } from './relay';

const WEB_SUFFIX_RE = /-web-[0-9a-f]{8}$/;

/** Drop `-web-XXXXXXXX` workers when a mobile counterpart exists for
 *  the same `technicianId`; if only the web entry exists, normalise
 *  its id to the bare technicianId so callers don't have to think
 *  about the sync-internal worker id shape. Exported for tests. */
export function dedupWorkers(list: WorkerListEntry[]): WorkerListEntry[] {
  const byBase = new Map<string, WorkerListEntry>();
  for (const w of list) {
    const base = w.id.replace(WEB_SUFFIX_RE, '');
    const isWeb = base !== w.id;
    const existing = byBase.get(base);
    if (!existing) {
      byBase.set(base, isWeb ? { ...w, id: base } : w);
    } else if (!isWeb) {
      // A canonical (mobile) entry trumps a previously-seen web entry.
      byBase.set(base, w);
    }
    // else: existing wins — either we already have the mobile entry
    // (incoming web is a duplicate), or we already have the first web
    // entry and no mobile counterpart has shown up yet (don't replace).
  }
  return Array.from(byBase.values());
}

export function useWorkers(): WorkerListEntry[] {
  const [workers, setWorkers] = useState<WorkerListEntry[]>([]);

  useEffect(() => {
    // The relay singleton may already have a cached list (the
    // identify sets it on connect), but the simpler contract is
    // "subscribe and accept whatever the next push delivers".
    // RelayClient also auto-fires `requestWorkers()` on (re)connect,
    // so a fresh subscription always gets a roster within
    // milliseconds of mount.
    const off = relay.onWorkers((list) => setWorkers(dedupWorkers(list)));
    // On mount, ask explicitly in case the connection has been up
    // long enough that we missed the first auto-fire.
    relay.requestWorkers();
    return off;
  }, []);

  return workers;
}
